import type { DispatchRouteEtaReport } from '../hooks/useRouteEta';

/**
 * The phone search view (#2678), pure. The search covers today and later only
 * (owner decision, 2026-10-14): a caller asks about a delivery still to come.
 */

/** The same floor as the global search palette (`search.minChars`). */
export const PHONE_SEARCH_MIN_CHARS = 2;
export const PHONE_SEARCH_PAGE_SIZE = 100;

/** `/api/fulfillment-jobs` params: one page, today and later, earliest first. */
export function buildPhoneSearchParams(needle: string, today: string) {
  return {
    search: needle,
    size: PHONE_SEARCH_PAGE_SIZE,
    sort: 'shipping_date:asc',
    shipping_date: `from:${today}`,
  };
}

/** Today's rows and later rows, each in the server's order. */
export function splitByDay<T extends { shipping_date: string }>(
  rows: readonly T[],
  today: string,
): { today: T[]; later: T[] } {
  return {
    today: rows.filter((row) => row.shipping_date === today),
    later: rows.filter((row) => row.shipping_date > today),
  };
}

export interface SearchEta {
  /** The task is delivered: `ms` is its completion time, else its ETA. */
  done: boolean;
  ms: number | null;
}

/** Every task on today's routes by Onfleet task id, the delivery job's join key. */
export function etaByTaskId(report: DispatchRouteEtaReport | undefined): Map<string, SearchEta> {
  const byTask = new Map<string, SearchEta>();
  for (const driver of report?.drivers ?? []) {
    for (const task of driver.tasks) {
      byTask.set(task.taskId, { done: false, ms: task.estimatedCompletionTime });
    }
    for (const task of driver.completed) {
      byTask.set(task.taskId, { done: true, ms: task.completedAt });
    }
  }
  return byTask;
}

/** The last four digits of a phone in any format, or `null` under four. */
export function phoneTail(phone: string | null | undefined): string | null {
  const digits = (phone ?? '').replace(/[^0-9]/g, '');
  return digits.length < 4 ? null : digits.slice(-4);
}

/** `YYYY-MM-DD` (or a datetime starting with it) → `MM/DD`; other input is returned unchanged. */
export function monthDay(date: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(date)) return date;
  return `${date.slice(5, 7)}/${date.slice(8, 10)}`;
}
